'use client'

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { AxiosError } from 'axios'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import {
  IconGripVertical,
  IconPlus,
  IconSearch,
  IconX,
} from '@tabler/icons-react'
import {
  getTestByHeading,
  type HeadingResItem,
  useGetHeadings,
} from '@/services/heading/useGetTestByHeading'
import {
  type UpdateHeadingBody,
  useUpdateHeading,
} from '@/services/heading/useUpdateHeading'
import { practiceTestService } from '@/services/practiceTestService'
import { Meta } from '@/types/common'
import { CreatorType, PracticeTestStatus } from '@/types/practice-test'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
  type DropAnimation,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  defaultAnimateLayoutChanges,
  useSortable,
  verticalListSortingStrategy,
  type AnimateLayoutChanges,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Loader2 } from 'lucide-react'
import { createPortal } from 'react-dom'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'

interface ArrangeTestItem {
  id: string | number
  name: string
}

interface ArrangeTestModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Class whose practice tests are listed (same API as PracticeTestTable). */
  classId: string
  creatorType?: CreatorType | null
  status?: PracticeTestStatus | null
  /** When false, loads and saves mock-exam heading layout (`is_practice: false`). */
  isPractice?: boolean
  onSave?: (payload: UpdateHeadingBody) => void
}

interface HeadingGroup {
  id: string
  title: string
  testIds: Array<string | number>
}

const AVAILABLE_CONTAINER_ID = 'available'
const headingContainerId = (headingId: string) => `heading:${headingId}`
/** Pool column: same test can also appear under headings — unique id per column. */
const availDraggableId = (testId: string | number) => `avail:${String(testId)}`
/** Heading row: unique per (heading, test) so one test can sit in multiple headings. */
const headingRowDraggableId = (headingId: string, testId: string | number) =>
  `hrow:${headingId}:${String(testId)}`

const parseAvailDraggableId = (id: string): string | null =>
  id.startsWith('avail:') ? id.slice('avail:'.length) : null

const parseHeadingRowDraggableId = (
  id: string
): { headingId: string; testId: string } | null => {
  if (!id.startsWith('hrow:')) return null
  const rest = id.slice('hrow:'.length)
  const idx = rest.indexOf(':')
  if (idx === -1) return null
  return { headingId: rest.slice(0, idx), testId: rest.slice(idx + 1) }
}

const parseAnyTestIdFromDraggable = (id: string): string | null =>
  parseAvailDraggableId(id) ?? parseHeadingRowDraggableId(id)?.testId ?? null

const headingSortDraggableId = (headingId: string) => `hsort:${headingId}`
const parseHeadingSortDraggableId = (id: string): string | null =>
  id.startsWith('hsort:') ? id.slice('hsort:'.length) : null

const isOthersHeading = (title: string) =>
  title.trim().toLowerCase() === 'others'

function collectDuplicateHeadingIds(headings: HeadingGroup[]): Set<string> {
  const seen = new Map<string, string>()
  const dupes = new Set<string>()
  for (const h of headings) {
    const key = h.title.trim().toLowerCase()
    if (!key) continue
    const prev = seen.get(key)
    if (prev) {
      dupes.add(prev)
      dupes.add(h.id)
    } else {
      seen.set(key, h.id)
    }
  }
  return dupes
}

/**
 * When dragging a heading, only consider other heading sortable targets.
 * When dragging a test, exclude heading sortable targets so tests don't
 * accidentally land on them.
 */
const headingAwareCollision: CollisionDetection = (args) => {
  const activeId = String(args.active.id)
  const isDraggingHeading = parseHeadingSortDraggableId(activeId) !== null

  if (isDraggingHeading) {
    return closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter(
        (c) => parseHeadingSortDraggableId(String(c.id)) !== null
      ),
    })
  }

  return closestCenter({
    ...args,
    droppableContainers: args.droppableContainers.filter(
      (c) => parseHeadingSortDraggableId(String(c.id)) === null
    ),
  })
}

/** Suppress the bouncy spring-back on placeholders during/after drag. */
const smoothLayoutChanges: AnimateLayoutChanges = (args) =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true })

const DROP_ANIMATION: DropAnimation = {
  duration: 200,
  easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
}

function coerceStoredTestId(s: string): string | number {
  const n = Number(s)
  return Number.isNaN(n) ? s : n
}

const DEFAULT_HEADING_GROUPS: HeadingGroup[] = [
  { id: '1', title: 'Heading 1', testIds: [] },
]

function DroppableContainer({
  id,
  className,
  children,
}: {
  id: string
  className?: string
  children: React.ReactNode
}) {
  const { setNodeRef, isOver } = useDroppable({ id })
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'rounded-xl border border-[#EAECF0] bg-white',
        isOver && 'ring-2 ring-[#0057B8]/30',
        className
      )}
    >
      {children}
    </div>
  )
}

function SortableTestRow({
  id,
  title,
  secondaryText,
  className,
}: {
  id: string
  title: string
  secondaryText?: string
  className?: string
}) {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, animateLayoutChanges: smoothLayoutChanges })

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    willChange: transform ? 'transform' : undefined,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex touch-none items-center justify-between gap-3 rounded-lg border border-[#EAECF0] bg-white px-3 py-2',
        isDragging && 'z-10 opacity-60 shadow-md',
        className
      )}
    >
      <div className='min-w-0'>
        <div className='truncate text-sm font-medium text-[#002652]'>
          {title}
        </div>
        {secondaryText && (
          <div className='truncate text-xs text-[#667085]'>{secondaryText}</div>
        )}
      </div>
      <button
        type='button'
        ref={setActivatorNodeRef}
        className={cn(
          'text-[#98A2B3] hover:text-[#667085]',
          isDragging ? 'cursor-grabbing' : 'cursor-grab'
        )}
        aria-label='Drag'
        {...attributes}
        {...listeners}
      >
        <IconGripVertical size={18} />
      </button>
    </div>
  )
}

function HeadingGroupPanel({
  heading,
  testsById,
  onChangeTitle,
  onRemove,
  sortableId,
  isDragDisabled,
  duplicateName,
}: {
  heading: HeadingGroup
  testsById: Map<string, ArrangeTestItem>
  onChangeTitle: (val: string) => void
  onRemove: () => void
  sortableId: string
  isDragDisabled?: boolean
  duplicateName?: boolean
}) {
  const containerId = headingContainerId(heading.id)
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sortableId,
    disabled: isDragDisabled,
    animateLayoutChanges: smoothLayoutChanges,
  })

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    willChange: transform ? 'transform' : undefined,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'touch-none rounded-xl border border-[#EAECF0] bg-[#F9FAFB] p-3',
        isDragging && 'z-10 opacity-60 shadow-md'
      )}
    >
      <div className='mb-2 flex items-start justify-between gap-2'>
        {!isDragDisabled && (
          <button
            type='button'
            ref={setActivatorNodeRef}
            className={cn(
              'mt-2 text-[#98A2B3] hover:text-[#667085]',
              isDragging ? 'cursor-grabbing' : 'cursor-grab'
            )}
            aria-label='Drag heading'
            {...attributes}
            {...listeners}
          >
            <IconGripVertical size={18} />
          </button>
        )}
        <div className='w-full'>
          <input
            value={heading.title}
            onChange={(e) => onChangeTitle(e.target.value)}
            className={cn(
              'w-full rounded-md border bg-white px-3 py-2 text-sm font-medium text-[#002652] outline-none focus:ring-2',
              duplicateName
                ? 'border-red-400 focus:ring-red-300/30'
                : 'border-[#EAECF0] focus:ring-[#0057B8]/30'
            )}
            placeholder='Heading'
          />
          {duplicateName && (
            <p className='mt-1 text-xs text-red-500'>
              Heading name already exists
            </p>
          )}
        </div>
        {!isDragDisabled && (
          <button
            type='button'
            className='mt-1 inline-flex h-8 w-8 items-center justify-center rounded-md text-[#667085] hover:bg-white'
            aria-label='Remove heading'
            title='Remove heading'
            onClick={onRemove}
          >
            <IconX size={16} />
          </button>
        )}
      </div>

      <DroppableContainer
        id={containerId}
        className='min-h-[80px] bg-white p-2'
      >
        <SortableContext
          items={heading.testIds.map((tid) =>
            headingRowDraggableId(heading.id, tid)
          )}
          strategy={verticalListSortingStrategy}
        >
          <div className='flex max-h-[200px] flex-col gap-2 overflow-y-auto'>
            {heading.testIds.map((tid) => {
              const test = testsById.get(String(tid))
              if (!test) return null
              return (
                <SortableTestRow
                  key={String(tid)}
                  id={headingRowDraggableId(heading.id, tid)}
                  title={test.name}
                />
              )
            })}
          </div>
        </SortableContext>
      </DroppableContainer>
    </div>
  )
}

const ARRANGE_MODAL_PAGE_LIMIT = 100

/** API list is unfiltered; keep only admin + tutor tests in this modal. */
function isPracticeTestCreatedByAdminOrTutor(
  creatorType: string | null | undefined
): boolean {
  const v = String(creatorType ?? '').toLowerCase()
  if (v === '') return true
  return (
    v === CreatorType.COMMON ||
    v === CreatorType.TUTOR_CREATED ||
    v === CreatorType.TUTOR
  )
}

const ArrangeTestModal = ({
  open,
  onOpenChange,
  classId,
  creatorType = null,
  status,
  isPractice = true,
  onSave,
}: ArrangeTestModalProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    })
  )

  const { updateHeadingAsync, isUpdatingHeading } = useUpdateHeading()

  const [availableSearch, setAvailableSearch] = useState('')
  const wasModalOpenRef = useRef(false)
  const prevAvailableSearchRef = useRef(availableSearch)

  useEffect(() => {
    if (open) return
    setAvailableSearch('')
    prevAvailableSearchRef.current = ''
  }, [open])

  const {
    data: practiceTestsInfinite,
    isLoading: isLoadingTests,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: [
      'practiceTests',
      'arrangeModal',
      classId,
      creatorType,
      status,
      availableSearch,
      isPractice,
    ],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      practiceTestService.getPracticeTestList({
        class_id: Number(classId),
        page: pageParam as number,
        limit: ARRANGE_MODAL_PAGE_LIMIT,
        search: availableSearch?.trim(),
        is_practice: isPractice,
        creator_type: null,
        status: null,
      }),
    enabled: open && Boolean(classId),
    getNextPageParam: (lastPage) => {
      const meta = lastPage?.meta as Meta | undefined
      if (!meta) return undefined
      const current = Number(meta.current_page ?? 1)
      const total = Number(meta.total_pages ?? 1)
      return current < total ? current + 1 : undefined
    },
  })

  const {
    headings: headingList,
    isLoadingHeadings,
    errorHeadings,
  } = useGetHeadings({
    classId,
    enabled: open && Boolean(classId),
    page: 1,
    limit: ARRANGE_MODAL_PAGE_LIMIT,
    isPractice,
  })

  const {
    data: testsByHeadingResponses,
    isLoading: isLoadingTestsByHeadings,
    error: errorTestsByHeadings,
  } = useQuery({
    queryKey: [
      'arrangeModal',
      'testsByEachHeading',
      classId,
      headingList,
      isPractice,
    ],
    queryFn: () =>
      Promise.all(
        headingList!.map((h: HeadingResItem) =>
          getTestByHeading({
            headingId: String(h.id),
            page: 1,
            limit: ARRANGE_MODAL_PAGE_LIMIT,
            status: undefined,
            /** Never filter by pool search — headings must stay complete. */
            search: '',
            creator_type: null,
            isPractice,
          })
        )
      ),
    enabled: open && Boolean(classId) && Boolean(headingList?.length),
  })

  const tests: ArrangeTestItem[] = useMemo(() => {
    const pages = practiceTestsInfinite?.pages ?? []
    const seen = new Set<string>()
    const out: ArrangeTestItem[] = []
    for (const p of pages) {
      for (const t of p?.data ?? []) {
        if (!isPracticeTestCreatedByAdminOrTutor(t.creator_type)) continue
        const idStr = String(t.id)
        if (seen.has(idStr)) continue
        seen.add(idStr)
        out.push({ id: t.id, name: t.name })
      }
    }
    return out
  }, [practiceTestsInfinite?.pages])

  const testsById = useMemo(() => {
    const m = new Map<string, ArrangeTestItem>()
    const responses = testsByHeadingResponses ?? []
    for (const resp of responses) {
      const raw = resp?.data
      if (!Array.isArray(raw)) continue
      for (const t of raw) {
        if (!isPracticeTestCreatedByAdminOrTutor(t.creator_type)) continue
        const idStr = String(t.id)
        if (!m.has(idStr)) {
          m.set(idStr, { id: t.id, name: t.name })
        }
      }
    }
    for (const t of tests) {
      m.set(String(t.id), t)
    }
    return m
  }, [tests, testsByHeadingResponses])

  const [headings, setHeadings] = useState<HeadingGroup[]>(
    DEFAULT_HEADING_GROUPS
  )
  const [availableIds, setAvailableIds] = useState<Array<string | number>>([])
  const [activeId, setActiveId] = useState<string | null>(null)

  useLayoutEffect(() => {
    if (!open) return
    if (isLoadingHeadings) return
    if (errorHeadings) {
      setHeadings(DEFAULT_HEADING_GROUPS)
      return
    }
    if (!headingList?.length) {
      setHeadings([])
      return
    }
    if (isLoadingTestsByHeadings) return
    if (errorTestsByHeadings) {
      setHeadings(DEFAULT_HEADING_GROUPS)
      return
    }
    const responses = testsByHeadingResponses ?? []
    setHeadings(
      headingList.map((h: HeadingResItem, i: number) => {
        const raw = responses[i]?.data
        const tests = Array.isArray(raw) ? raw : []
        return {
          id: String(h.id),
          title: h.name,
          testIds: tests
            .filter((t: { creator_type?: string | null }) =>
              isPracticeTestCreatedByAdminOrTutor(t.creator_type)
            )
            .map((t: { id: string | number }) =>
              coerceStoredTestId(String(t.id))
            ),
        }
      })
    )
  }, [
    open,
    headingList,
    isLoadingHeadings,
    errorHeadings,
    testsByHeadingResponses,
    isLoadingTestsByHeadings,
    errorTestsByHeadings,
    isPractice,
  ])

  // Full pool on the right: every test stays visible so the same test can be added to multiple headings.
  useEffect(() => {
    if (!open) {
      wasModalOpenRef.current = false
      return
    }
    const justOpened = !wasModalOpenRef.current
    wasModalOpenRef.current = true
    const searchChanged = prevAvailableSearchRef.current !== availableSearch
    prevAvailableSearchRef.current = availableSearch

    const fromApi = tests.map((t) => t.id)
    if (justOpened || searchChanged) {
      setAvailableIds(fromApi)
      return
    }
    setAvailableIds((prev) => {
      const prevSet = new Set(prev.map(String))
      const kept = prev.filter((id) =>
        fromApi.some((x) => String(x) === String(id))
      )
      const appended = fromApi.filter((id) => !prevSet.has(String(id)))
      return [...kept, ...appended]
    })
  }, [open, availableSearch, tests])

  const allContainerIds = useMemo(() => {
    return [
      AVAILABLE_CONTAINER_ID,
      ...headings.map((h) => headingContainerId(h.id)),
    ]
  }, [headings])

  const itemsByContainerId = useMemo(() => {
    const m = new Map<string, string[]>()
    m.set(AVAILABLE_CONTAINER_ID, availableIds.map(availDraggableId))
    for (const h of headings) {
      m.set(
        headingContainerId(h.id),
        h.testIds.map((tid) => headingRowDraggableId(h.id, tid))
      )
    }
    return m
  }, [availableIds, headings])

  const filteredAvailableIds = useMemo(() => {
    const q = availableSearch.trim().toLowerCase()
    if (!q) return availableIds
    return availableIds.filter((tid) => {
      const test = testsById.get(String(tid))
      return (test?.name ?? '').toLowerCase().includes(q)
    })
  }, [availableIds, availableSearch, testsById])

  const findContainerForItem = (draggableId: string) => {
    const avail = parseAvailDraggableId(draggableId)
    if (avail !== null) return AVAILABLE_CONTAINER_ID
    const hrow = parseHeadingRowDraggableId(draggableId)
    if (hrow) return headingContainerId(hrow.headingId)
    for (const cid of allContainerIds) {
      const items = itemsByContainerId.get(cid) ?? []
      if (items.includes(draggableId)) return cid
    }
    return null
  }

  const resolveDropContainer = (overId: string): string | null => {
    if (allContainerIds.includes(overId)) return overId
    const fromItem = findContainerForItem(overId)
    if (fromItem) return fromItem
    if (overId.startsWith('heading:')) return overId
    return null
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const active = String(event.active.id)
    const over = event.over ? String(event.over.id) : null
    setActiveId(null)
    if (!over) return

    const activeHeadingSortId = parseHeadingSortDraggableId(active)
    if (activeHeadingSortId) {
      const overHeadingSortId = parseHeadingSortDraggableId(over)
      if (!overHeadingSortId) return
      setHeadings((prev) => {
        const oldIndex = prev.findIndex((h) => h.id === activeHeadingSortId)
        const newIndex = prev.findIndex((h) => h.id === overHeadingSortId)
        if (oldIndex === -1 || newIndex === -1) return prev
        return arrayMove(prev, oldIndex, newIndex)
      })
      return
    }

    const fromContainer = findContainerForItem(active)
    const toContainer = resolveDropContainer(over)

    if (!fromContainer || !toContainer) return

    const fromItems = itemsByContainerId.get(fromContainer) ?? []
    const toItems = itemsByContainerId.get(toContainer) ?? []
    const fromIndex = fromItems.indexOf(active)
    const toIndex = toItems.indexOf(over)

    const mapAvailIdsToTestIds = (ids: string[]) =>
      ids.map((id) => parseAvailDraggableId(id)).filter(Boolean) as string[]

    const mapHeadingRowIdsToTestIds = (headingId: string, ids: string[]) =>
      ids
        .map((id) => parseHeadingRowDraggableId(id))
        .filter((p): p is { headingId: string; testId: string } => {
          if (!p) return false
          return p.headingId === headingId
        })
        .map((p) => p.testId)

    if (fromContainer === toContainer) {
      if (
        fromContainer === AVAILABLE_CONTAINER_ID &&
        availableSearch.trim().length > 0
      ) {
        return
      }
      const next = arrayMove(fromItems, fromIndex, Math.max(0, toIndex))
      if (fromContainer === AVAILABLE_CONTAINER_ID) {
        setAvailableIds(mapAvailIdsToTestIds(next).map(coerceStoredTestId))
      } else if (fromContainer.startsWith('heading:')) {
        const headingId = fromContainer.slice('heading:'.length)
        setHeadings((prev) =>
          prev.map((h) =>
            h.id === headingId
              ? {
                  ...h,
                  testIds: mapHeadingRowIdsToTestIds(headingId, next).map(
                    coerceStoredTestId
                  ),
                }
              : h
          )
        )
      }
      return
    }

    // From pool → heading: copy (keep item on the right); same test can appear under several headings.
    if (
      fromContainer === AVAILABLE_CONTAINER_ID &&
      toContainer.startsWith('heading:')
    ) {
      const movedTestId = parseAvailDraggableId(active)
      if (!movedTestId) return
      const hid = toContainer.slice('heading:'.length)
      const insertAt =
        availableSearch.trim().length > 0
          ? Math.max(0, toItems.length)
          : toIndex >= 0
            ? toIndex
            : Math.max(0, toItems.length)
      setHeadings((prev) =>
        prev.map((h) => {
          if (h.id !== hid) return h
          if (h.testIds.some((t) => String(t) === movedTestId)) return h
          const rowId = headingRowDraggableId(hid, movedTestId)
          const nextRowIds = [
            ...toItems.slice(0, insertAt),
            rowId,
            ...toItems.slice(insertAt),
          ]
          return {
            ...h,
            testIds: mapHeadingRowIdsToTestIds(hid, nextRowIds).map(
              coerceStoredTestId
            ),
          }
        })
      )
      return
    }

    // From heading → pool: remove from heading only (pool list unchanged).
    if (
      fromContainer.startsWith('heading:') &&
      toContainer === AVAILABLE_CONTAINER_ID
    ) {
      const nextFrom = fromItems.filter((x) => x !== active)
      const hid = fromContainer.slice('heading:'.length)
      setHeadings((prev) =>
        prev.map((h) =>
          h.id === hid
            ? {
                ...h,
                testIds: mapHeadingRowIdsToTestIds(hid, nextFrom).map(
                  coerceStoredTestId
                ),
              }
            : h
        )
      )
      return
    }

    // Move between headings.
    const fromHrow = parseHeadingRowDraggableId(active)
    if (!fromHrow) return
    const movedTestId = fromHrow.testId
    const nextFrom = fromItems.filter((x) => x !== active)
    const insertAt =
      toIndex >= 0 ? toIndex : Math.max(0, (toItems ?? []).length)
    const toHid = toContainer.slice('heading:'.length)
    const newRowId = headingRowDraggableId(toHid, movedTestId)
    const nextTo = [
      ...toItems.slice(0, insertAt),
      newRowId,
      ...toItems.slice(insertAt),
    ]

    setHeadings((prev) => {
      const targetAlreadyHas = prev.some(
        (h) =>
          h.id === toHid &&
          h.testIds.some((t) => String(t) === String(movedTestId))
      )
      if (targetAlreadyHas) {
        return prev.map((h) =>
          h.id === fromHrow.headingId
            ? {
                ...h,
                testIds: mapHeadingRowIdsToTestIds(
                  fromHrow.headingId,
                  nextFrom
                ).map(coerceStoredTestId),
              }
            : h
        )
      }
      return prev.map((h) => {
        if (h.id === fromHrow.headingId) {
          return {
            ...h,
            testIds: mapHeadingRowIdsToTestIds(
              fromHrow.headingId,
              nextFrom
            ).map(coerceStoredTestId),
          }
        }
        if (h.id === toHid) {
          const ids = mapHeadingRowIdsToTestIds(toHid, nextTo).map(
            coerceStoredTestId
          )
          const deduped = ids.filter(
            (tid, i) => ids.findIndex((x) => String(x) === String(tid)) === i
          )
          return { ...h, testIds: deduped }
        }
        return h
      })
    })
  }

  const addHeading = () => {
    setHeadings((prev) => {
      const nextId = String(
        prev.reduce((max, h) => Math.max(max, Number(h.id) || 0), 0) + 1
      )
      return [{ id: nextId, title: ``, testIds: [] }, ...prev]
    })
  }

  const handleSave = async () => {
    if (headings.some((h) => !h.title.trim())) {
      toast.error('Please enter a title for every heading.', {
        duration: 3000,
        position: 'top-right',
      })
      return
    }
    if (collectDuplicateHeadingIds(headings).size > 0) {
      toast.error('Heading titles must be unique.', {
        duration: 3000,
        position: 'top-right',
      })
      return
    }

    const payload: UpdateHeadingBody = {
      items: headings.map((h, index) => {
        const idNum = Number(h.id)
        return {
          id: Number.isFinite(idNum) ? idNum : undefined,
          name: h.title,
          position: index + 1,
          practice_tests: h.testIds.map((tid, i) => ({
            practice_test_id: Number(tid),
            position: i + 1,
          })),
        }
      }),
    }

    try {
      await updateHeadingAsync({ classId, payload, isPractice })
      toast.success('Update heading successfully')
      onSave?.(payload)
      onOpenChange(false)
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMsg =
          (error.response?.data as { detail?: string })?.detail ||
          'Failed to upload file'
        toast.error(errorMsg, {
          duration: 3000,
          position: 'top-right',
        })
        console.log(error.response)
      }
      console.log(error)
    }
  }

  const activeTest = useMemo(() => {
    if (!activeId) return null
    const tid = parseAnyTestIdFromDraggable(activeId)
    if (!tid) return null
    return testsById.get(String(tid)) ?? null
  }, [activeId, testsById])

  const activeHeading = useMemo(() => {
    if (!activeId) return null
    const hid = parseHeadingSortDraggableId(activeId)
    if (!hid) return null
    return headings.find((h) => h.id === hid) ?? null
  }, [activeId, headings])

  const duplicateHeadingIds = useMemo(
    () => collectDuplicateHeadingIds(headings),
    [headings]
  )

  const hasDuplicateHeadings = duplicateHeadingIds.size > 0
  const hasBlankHeading = headings.some((h) => !h.title.trim())

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='gap-4 px-0 pt-5 sm:max-w-4xl'>
        <div className='border-b border-[#EAECF0] px-6 pb-4'>
          <DialogHeader className='text-left'>
            <DialogTitle className='text-[20px] font-medium text-[#002652]'>
              Arrange Tests
            </DialogTitle>
          </DialogHeader>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={headingAwareCollision}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className='grid max-h-[500px] gap-4 px-6 md:grid-cols-2 md:items-start'>
            <div className='flex max-h-[500px] min-h-0 flex-col gap-3'>
              <div className='shrink-0'>
                <div className='text-sm font-semibold text-[#002652]'>
                  Heading groups
                </div>
                <div className='text-xs text-[#667085]'>
                  Drag tests into headings and reorder. The same test can appear
                  under more than one heading.
                </div>
              </div>

              {!isLoadingHeadings && !isLoadingTestsByHeadings && (
                <button
                  type='button'
                  className='flex w-full shrink-0 items-center justify-center gap-2 rounded-xl border border-dashed border-[#D0D5DD] bg-white px-3 py-3 text-sm font-medium text-[#344054] hover:bg-[#F9FAFB]'
                  onClick={addHeading}
                >
                  <IconPlus size={16} /> Add more heading
                </button>
              )}

              <div className='min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-y-contain pr-1'>
                {isLoadingHeadings || isLoadingTestsByHeadings ? (
                  <div className='rounded-xl border border-[#EAECF0] bg-white px-3 py-8 text-center text-sm text-[#667085]'>
                    Loading heading groups…
                  </div>
                ) : errorHeadings || errorTestsByHeadings ? (
                  <div className='rounded-xl border border-[#EAECF0] bg-white px-3 py-4 text-center text-sm text-red-600'>
                    Could not load saved heading groups. You can still add
                    headings below.
                  </div>
                ) : null}

                {!isLoadingHeadings && !isLoadingTestsByHeadings && (
                  <SortableContext
                    items={headings.map((h) => headingSortDraggableId(h.id))}
                    strategy={verticalListSortingStrategy}
                  >
                    {headings.map((h) => (
                      <HeadingGroupPanel
                        key={h.id}
                        heading={h}
                        testsById={testsById}
                        sortableId={headingSortDraggableId(h.id)}
                        isDragDisabled={isOthersHeading(h.title)}
                        duplicateName={duplicateHeadingIds.has(h.id)}
                        onChangeTitle={(val) =>
                          setHeadings((prev) =>
                            prev.map((x) =>
                              x.id === h.id ? { ...x, title: val } : x
                            )
                          )
                        }
                        onRemove={() =>
                          setHeadings((prev) =>
                            prev.filter((x) => x.id !== h.id)
                          )
                        }
                      />
                    ))}
                  </SortableContext>
                )}
              </div>
            </div>

            <div className='flex max-h-[500px] min-h-0 flex-col gap-3'>
              <div className='shrink-0'>
                <div className='text-sm font-semibold text-[#002652]'>
                  Available Tests
                </div>
                <div className='text-xs text-[#667085]'>
                  Tests stay listed here; dragging adds a copy under a heading.
                </div>
              </div>

              <div className='relative shrink-0'>
                <IconSearch className='pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-[#98A2B3]' />
                <Input
                  value={availableSearch}
                  onChange={(e) => setAvailableSearch(e.target.value)}
                  placeholder='Search test'
                  className='h-10 pl-9'
                />
              </div>

              <DroppableContainer
                id={AVAILABLE_CONTAINER_ID}
                className='min-h-0 flex-1 overflow-y-auto overscroll-y-contain p-3 pr-2'
              >
                {isLoadingTests ? (
                  <div className='py-8 text-center text-sm text-[#667085]'>
                    Loading tests…
                  </div>
                ) : (
                  <>
                    <SortableContext
                      items={filteredAvailableIds.map(availDraggableId)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className='flex flex-col gap-2'>
                        {filteredAvailableIds.map((tid) => {
                          const test = testsById.get(String(tid))
                          if (!test) return null
                          return (
                            <SortableTestRow
                              key={String(tid)}
                              id={availDraggableId(tid)}
                              title={test.name}
                            />
                          )
                        })}
                      </div>
                    </SortableContext>
                    {hasNextPage ? (
                      <div className='mt-3 flex justify-center'>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          className='border-[#D0D5DD] text-[#344054]'
                          disabled={isFetchingNextPage}
                          onClick={() => fetchNextPage()}
                        >
                          {isFetchingNextPage ? (
                            <>
                              <Loader2 className='mr-2 size-4 animate-spin' />
                              Loading…
                            </>
                          ) : (
                            'Load more'
                          )}
                        </Button>
                      </div>
                    ) : null}
                  </>
                )}
              </DroppableContainer>
            </div>
          </div>

          {typeof document !== 'undefined'
            ? createPortal(
                <DragOverlay
                  dropAnimation={DROP_ANIMATION}
                  style={{ cursor: 'grabbing' }}
                >
                  {activeHeading ? (
                    <div className='rounded-xl border border-[#EAECF0] bg-[#F9FAFB] px-4 py-3 shadow-lg'>
                      <div className='text-sm font-semibold text-[#002652]'>
                        {activeHeading.title}
                      </div>
                      <div className='mt-1 text-xs text-[#667085]'>
                        {activeHeading.testIds.length} test(s)
                      </div>
                    </div>
                  ) : activeTest ? (
                    <div className='rounded-lg border border-[#EAECF0] bg-white px-3 py-2 shadow-lg'>
                      <div className='text-sm font-medium text-[#002652]'>
                        {activeTest.name}
                      </div>
                    </div>
                  ) : null}
                </DragOverlay>,
                document.body
              )
            : null}
        </DndContext>

        <DialogFooter className='border-t border-[#EAECF0] px-6 py-4'>
          <Button
            variant='outline'
            className='border border-[#EAECF0] bg-white text-[#475467] hover:bg-transparent'
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            className='relative !min-w-[120px]'
            disabled={
              isUpdatingHeading || hasDuplicateHeadings || hasBlankHeading
            }
            onClick={handleSave}
          >
            <span className={isUpdatingHeading ? 'opacity-0' : ''}>
              Save Changes
            </span>
            {isUpdatingHeading ? (
              <Loader2 className='absolute animate-spin' />
            ) : null}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ArrangeTestModal
